import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import type { AccountDeletionRequest, Json } from '@/types/database.types';

type AccountDeletionContext = {
  enabled: boolean;
  userId?: string;
  canReview: boolean;
};

export function useAccountDeletion({ enabled, userId, canReview }: AccountDeletionContext) {
  const queryClient = useQueryClient();
  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['account-deletion'] });
    void queryClient.invalidateQueries({ queryKey: ['audit-log'] });
    void queryClient.invalidateQueries({ queryKey: ['notifications'] });
  };

  const myRequest = useQuery({
    queryKey: ['account-deletion', 'mine', userId],
    enabled: enabled && !!userId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_deletion_requests')
        .select('*')
        .eq('requester_user_id', userId!)
        .order('requested_at', { ascending: false })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as AccountDeletionRequest | null;
    },
  });

  const reviewQueue = useQuery({
    queryKey: ['account-deletion', 'review-queue'],
    enabled: enabled && canReview,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('account_deletion_requests')
        .select('*')
        .eq('status', 'PENDING_REVIEW')
        .order('requested_at', { ascending: true });

      if (error) throw error;
      return (data ?? []) as AccountDeletionRequest[];
    },
  });

  const requestDeletion = useMutation({
    mutationFn: async ({
      organizationId,
      reason,
      requestExport,
    }: {
      organizationId: string | null;
      reason: string;
      requestExport: boolean;
    }) => {
      const { data, error } = await supabase.rpc('request_my_account_deletion', {
        p_organization_id: organizationId,
        p_reason: reason || null,
        p_request_export: requestExport,
      });
      if (error) throw error;
      return data;
    },
    onSuccess: invalidate,
  });

  const cancelDeletion = useMutation({
    mutationFn: async (requestId: string) => {
      const { error } = await supabase.rpc('cancel_my_account_deletion', { p_request_id: requestId });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const reviewDeletion = useMutation({
    mutationFn: async ({ requestId, decision, note }: { requestId: string; decision: 'APPROVE' | 'REJECT'; note?: string }) => {
      const { error } = await supabase.rpc('review_account_deletion', {
        p_request_id: requestId,
        p_decision: decision,
        p_review_note: note || null,
      });
      if (error) throw error;
    },
    onSuccess: invalidate,
  });

  const exportData = useMutation({
    mutationFn: async (organizationId: string | null) => {
      const { data, error } = await supabase.rpc('export_my_account_data', {
        p_organization_id: organizationId,
      });
      if (error) throw error;
      return data as Json;
    },
    onSuccess: invalidate,
  });

  return {
    myRequest,
    reviewQueue,
    requestDeletion,
    cancelDeletion,
    reviewDeletion,
    exportData,
  };
}

export function downloadAccountDataExport(data: Json) {
  const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `worklynx-account-export-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}
